
import { Course } from './types';
import { ENHANCED_REACT_INTRO_MODULE, ENHANCED_REACT_COMPONENTS_MODULE } from './enhancedReactModules';
import { migrateQuickCheckToQuiz } from './safeQuizMigration';

// Enhanced React modules with comprehensive quiz standardization
const REACT_HOOKS_MODULE = migrateQuickCheckToQuiz({
  id: 'react-hooks',
  title: 'React Hooks',
  description: 'Learn React Hooks including useState, useEffect, and custom hooks.',
  content: `
# React Hooks

Hooks are functions that let you "hook into" React features from functional components.

## useState Hook

The useState Hook lets you add state to functional components:

\`\`\`jsx
import React, { useState } from 'react';

function Counter() {
  const [count, setCount] = useState(0);

  return (
    <div>
      <p>You clicked {count} times</p>
      <button onClick={() => setCount(count + 1)}>
        Click me
      </button>
    </div>
  );
}
\`\`\`

## useEffect Hook

The useEffect Hook lets you perform side effects in functional components:

\`\`\`jsx
import React, { useState, useEffect } from 'react';

function Example() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    document.title = \`You clicked \${count} times\`;
  });

  return (
    <div>
      <p>You clicked {count} times</p>
      <button onClick={() => setCount(count + 1)}>
        Click me
      </button>
    </div>
  );
}
\`\`\`

## Custom Hooks

Create your own Hooks to reuse stateful logic:

\`\`\`jsx
function useCounter(initialValue = 0) {
  const [count, setCount] = useState(initialValue);

  const increment = () => setCount(count + 1);
  const decrement = () => setCount(count - 1);
  const reset = () => setCount(initialValue);

  return { count, increment, decrement, reset };
}
\`\`\`
  `,
  duration: '75 minutes',
  difficulty: 'intermediate',
  order: 3,
  estimatedTime: '75 minutes',
  objectives: [
    'Understand what React Hooks are and why they were introduced',
    'Master useState for managing component state',
    'Learn useEffect for handling side effects',
    'Create custom hooks for reusable logic'
  ],
  learningObjectives: [
    'Understand what React Hooks are and why they were introduced',
    'Master useState for managing component state',
    'Learn useEffect for handling side effects',
    'Create custom hooks for reusable logic'
  ],
  quickCheck: {
    questions: [
      {
        id: 'react-hooks-1',
        question: 'What are React Hooks?',
        options: [
          'Functions that let you use state and other React features in functional components',
          'Event handlers for user interactions',
          'CSS selectors for styling components',
          'Database connection methods'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      },
      {
        id: 'react-hooks-2',
        question: 'Which Hook is used to add state to functional components?',
        options: [
          'useState',
          'useEffect',
          'useContext',
          'useReducer'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      },
      {
        id: 'react-hooks-3',
        question: 'When does the useEffect hook run by default?',
        options: [
          'After every render',
          'Only on component mount',
          'Only on component unmount',
          'Never'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      }
    ],
    passThreshold: 75
  }
});

const REACT_STATE_MODULE = migrateQuickCheckToQuiz({
  id: 'react-state',
  title: 'State Management',
  description: 'Learn how to manage state in React applications effectively.',
  content: `
# State Management in React

State is data that can change over time and affects what a component renders.

## What is State?

State is a built-in React object used to store data that belongs to a component and can change over time. When state changes, the component re-renders.

## useState in Detail

\`\`\`jsx
const [state, setState] = useState(initialValue);
\`\`\`

- \`state\`: Current state value
- \`setState\`: Function to update state
- \`initialValue\`: Initial state value

## State Updates

State updates are asynchronous and may be batched:

\`\`\`jsx
function Counter() {
  const [count, setCount] = useState(0);

  const handleClick = () => {
    // This will only increment by 1, not 2
    setCount(count + 1);
    setCount(count + 1);
  };

  // Correct way using functional update
  const handleClickCorrect = () => {
    setCount(prevCount => prevCount + 1);
    setCount(prevCount => prevCount + 1);
  };

  return (
    <button onClick={handleClickCorrect}>
      Count: {count}
    </button>
  );
}
\`\`\`

## Managing Complex State

For complex state objects, use the spread operator:

\`\`\`jsx
const [user, setUser] = useState({
  name: '',
  email: '',
  age: 0
});

const updateUser = (field, value) => {
  setUser(prevUser => ({
    ...prevUser,
    [field]: value
  }));
};
\`\`\`
  `,
  duration: '60 minutes',
  difficulty: 'intermediate',
  order: 4,
  estimatedTime: '60 minutes',
  objectives: [
    'Understand what state is and why it\'s important',
    'Learn to use useState effectively',
    'Handle state updates correctly',
    'Manage complex state objects'
  ],
  learningObjectives: [
    'Understand what state is and why it\'s important',
    'Learn to use useState effectively',
    'Handle state updates correctly',
    'Manage complex state objects'
  ],
  quickCheck: {
    questions: [
      {
        id: 'react-state-1',
        question: 'What is state in React?',
        options: [
          'A built-in object that stores component data that can change over time',
          'The current condition of the application',
          'A way to style components',
          'The loading status of a component'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      },
      {
        id: 'react-state-2',
        question: 'What happens when state changes?',
        options: [
          'The component re-renders',
          'The page reloads',
          'The browser crashes',
          'Nothing happens'
        ],
        correctAnswer: 0,
        correctAnswerIndex: 0
      }
    ],
    passThreshold: 75
  }
});

export const IMPROVED_BASIC_COURSE: Course = {
  id: 'basic-react',
  title: 'React Fundamentals',
  description: 'Master the fundamentals of React development with hands-on projects and comprehensive assessments.',
  level: 'Beginner',
  totalDuration: '4 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Basic knowledge of HTML, CSS, and JavaScript',
    'Understanding of ES6+ features',
    'Familiarity with modern web development concepts'
  ],
  learningOutcomes: [
    'Build interactive React applications from scratch',
    'Understand component-based architecture',
    'Master React Hooks and state management',
    'Apply modern React development best practices'
  ],
  modules: [
    ENHANCED_REACT_INTRO_MODULE,
    ENHANCED_REACT_COMPONENTS_MODULE,
    REACT_HOOKS_MODULE,
    REACT_STATE_MODULE
  ],
  type: 'react'
};
